import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Lock, Eye, EyeOff, Building2 } from "lucide-react";

const RAZ_LOGO_NEGATIVE = "https://static.manus.space/webdev/emiteai_social_dashboard/raz_logo_2025_negativo-reduzido_1_.png";

export default function AceitarConviteNovo() {
  const [, navigate] = useLocation();
  const { login: authLogin } = useAuth();
  // Get token from query string
  const token = new URLSearchParams(window.location.search).get("token") ?? "";

  const { data: invite, isLoading, error } = trpc.invites.verify.useQuery(
    { token },
    { enabled: !!token, retry: false }
  );

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [showPw, setShowPw] = useState(false);
  const [done, setDone] = useState(false);

  const acceptMut = trpc.invites.accept.useMutation({
    onSuccess: (data) => {
      authLogin({
        token:      data.token,
        userId:     data.userId,
        name:       data.name ?? "",
        email:      data.email,
        role:       (data.role ?? "viewer") as "admin" | "editor" | "viewer",
        clientId:   data.clientId ?? null,
        isRazAdmin: data.isRazAdmin ?? false,
        mustChangePassword: false,
      });
      setDone(true);
      toast.success("Conta criada com sucesso!");
      setTimeout(() => navigate("/"), 2000);
    },
    onError: (err) => toast.error(err.message),
  });

  const handleSubmit = () => {
    if (!form.password || form.password.length < 6) {
      toast.error("A senha deve ter pelo menos 6 caracteres");
      return;
    }
    if (form.password !== form.confirm) {
      toast.error("As senhas não coincidem");
      return;
    }
    acceptMut.mutate({ token, password: form.password, origin: window.location.origin });
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--solar-bg)]">
      <div className="w-full max-w-md space-y-6">
        {/* Logo */}
        <div className="text-center flex flex-col items-center gap-2">
          <div className="rounded-xl bg-[var(--solar-ink)] shadow-[var(--elev-2)] px-5 py-3 flex items-center justify-center">
            <img src={RAZ_LOGO_NEGATIVE} alt="RAZ" className="h-8" />
          </div>
          <p className="text-sm text-[var(--solar-mute)]">Plataforma de Inteligência Estratégica</p>
        </div>

        <div className="rounded-xl border border-[var(--solar-line)] bg-white p-8 shadow-[var(--elev-2)]">
          {isLoading ? (
            <div className="text-center py-8 space-y-3">
              <Loader2 className="w-10 h-10 mx-auto animate-spin text-[var(--brand)]" />
              <p className="text-sm text-[var(--solar-mute)]">Verificando convite...</p>
            </div>
          ) : !invite?.valid ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center mx-auto">
                <XCircle className="w-8 h-8 text-[var(--destructive)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--solar-ink)]">Convite inválido</h2>
                <p className="text-sm text-[var(--solar-mute)] mt-1">{invite?.reason ?? "Este link de convite não é válido."}</p>
              </div>
              <Button variant="outline" className="border-[var(--solar-line)] text-[var(--solar-ink)] hover:bg-[var(--secondary)]"
                onClick={() => navigate("/login")}>
                Ir para o login
              </Button>
            </div>
          ) : done ? (
            <div className="text-center py-8 space-y-4">
              <div className="w-16 h-16 rounded-full bg-[var(--brand-soft)] flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-[var(--brand)]" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-[var(--solar-ink)]">Conta criada!</h2>
                <p className="text-sm text-[var(--solar-mute)] mt-1">Redirecionando para o dashboard...</p>
              </div>
            </div>
          ) : (
            <div className="space-y-5">
              <div className="text-center">
                <h2 className="text-xl font-semibold text-[var(--solar-ink)]">Criar sua conta</h2>
                <p className="text-sm text-[var(--solar-mute)] mt-1">
                  Convite para <span className="text-[var(--solar-ink)] font-medium">{invite.email}</span>
                </p>
              </div>

              {invite.clientIds && invite.clientIds.length > 0 && (
                <div className="rounded-[10px] bg-[var(--brand-soft)] border border-[var(--solar-line)] p-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-[var(--brand)] flex-shrink-0" />
                  <p className="text-xs text-[var(--ink-2)]">
                    Você terá acesso a <span className="text-[var(--solar-ink)] font-medium">{invite.clientIds.length} cliente{invite.clientIds.length > 1 ? "s" : ""}</span> na plataforma
                  </p>
                </div>
              )}

              <div className="space-y-3">
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--solar-mute)]" />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="Mínimo 6 caracteres"
                      className="pl-9 pr-9 bg-white border-[var(--solar-line)] text-[var(--solar-ink)] placeholder:text-[var(--solar-mute)] focus:border-[var(--brand)]"
                      value={form.password}
                      onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    />
                    <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--solar-mute)] hover:text-[var(--solar-ink)]"
                      onClick={() => setShowPw(v => !v)}>
                      {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <label className="text-[11px] uppercase tracking-wide font-semibold text-[var(--solar-mute)]">Confirmar senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--solar-mute)]" />
                    <Input
                      type={showPw ? "text" : "password"}
                      placeholder="Repita a senha"
                      className="pl-9 bg-white border-[var(--solar-line)] text-[var(--solar-ink)] placeholder:text-[var(--solar-mute)] focus:border-[var(--brand)]"
                      value={form.confirm}
                      onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
                      onKeyDown={e => e.key === "Enter" && handleSubmit()}
                    />
                  </div>
                </div>
              </div>

              <Button className="w-full h-11 font-semibold bg-[var(--solar-ink)] text-white hover:opacity-90 shadow-[var(--elev-1)]"
                disabled={!form.password || !form.confirm || acceptMut.isPending}
                onClick={handleSubmit}>
                {acceptMut.isPending
                  ? <Loader2 className="w-4 h-4 animate-spin" />
                  : "Criar conta e acessar"}
              </Button>
            </div>
          )}
        </div>

        <p className="text-center text-xs text-[var(--solar-mute)]">
          RAZ Consulting · Plataforma de Inteligência Estratégica
        </p>
      </div>
    </div>
  );
}
